import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  line, Color, NEW_DOCUMENT_ID, Size, documentPageMode, changeOrderPills, pageKindLineText, t, type ChangeOrderAnswer,
} from '@bms2/shared';
import { Button } from '../../components/controls';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { Money } from '../../components/Money';
import { ReasonWindow } from '../../components/modals/ReasonWindow';
import { AsyncStatePanel } from '../../components/composites/AsyncStatePanel';
import { approvalActionPath } from '../approvals/PendingApprovalsPage';
import { refreshMenuCounts } from '../../shell/menuCounts';
import { AccessDenied } from '../../shell/Fallbacks';
import { useStatusResolver } from '../../shell/StatusResolver';
import { ChangeOrderForm } from './ChangeOrderForm';
import { ApprovalReason } from './ApprovalReason';
import { BannerPills } from './BannerPills';
import { DocumentFilesSection } from './DocumentFilesSection';
import { DocumentViewPage, type DocumentLinesDescriptor } from './DocumentViewPage';
import { CHANGE_ORDERS_PATH, post, useApi } from './api';
import { documentPdfPath } from './ViewPdfLink';
import { type ScreenFact } from './PageFacts';
import styles from './DocumentPage.module.css';
import { DealTeamPanel } from './DealTeamPanel';
import { useViewerZone } from '../../shell/ViewerZone';
import { DocumentNotes, carriedNotes } from './DocumentNotes';
import { useDayText } from '../../shell/viewerDates';

/** The change order's own route, which the parent order's list links to and its Raise opens (decision 0475). */
export const CHANGE_ORDER_ROUTE = '/change-orders';

/** The two axes as pills: the change order's own lifecycle and the gate's answer on it. */
/**
 * 🔒 The Change Order's own page (decision 0475): view mode draws what was
 * agreed after signing — the reason, the description and the lines with the
 * server's own total beneath them — and edit mode is the form. It is reached
 * from the parent Sales Order's change-order list and from the Raise control
 * there, which opens this page in edit mode the way `+ New` opens every other
 * document's (decision 0409).
 *
 * 🔨 The total, the status and the approval status are the server's answers and
 * are never recomputed here. A change order's lines can be negative, so a
 * screen-side sum would be the one place a removal could read as an addition.
 */
export function ChangeOrderPage({ changeOrderId, apiBaseUrl = '' }: { changeOrderId: string; apiBaseUrl?: string }) {
  const dayText = useDayText();
  // 🔒 The banner's pills read the shell's one status resolver, as the lists do (decision 0639).
  const resolveStatus = useStatusResolver();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // 🔒 The URL carries the mode, so a link opens the mode it names (decision 0409); `new` is the one Raise a change order opens.
  const creating = changeOrderId === NEW_DOCUMENT_ID;
  const editing = documentPageMode(params.get('mode'), changeOrderId) === 'edit';
  const page = useApi<ChangeOrderAnswer>(editing ? null : `${CHANGE_ORDERS_PATH}/${changeOrderId}`, apiBaseUrl);
  const zone = useViewerZone();
  const [open, setOpen] = useState<'withdraw' | 'reject' | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // 🔒 Each window holds its own reason and is gone once closed, so opening one starts empty (decision 2870).
  const openModal = (which: 'withdraw' | 'reject') => setOpen(which);

  if (editing) return <ChangeOrderForm changeOrderId={creating ? null : changeOrderId} apiBaseUrl={apiBaseUrl} />;
  if (page.denied) return <AccessDenied reason={page.deniedReason} />;

  const act = async (path: string, body: unknown, ok: string) => {
    setBusy(true);
    const answer = await post<unknown>(path, body, apiBaseUrl);
    setBusy(false);
    if (!answer.ok) return setNotice(`!${answer.message}`);
    setOpen(null);
    // 🔒 Decision 3072: every act here (Approve, Reject, Withdraw) takes the Change Order out of the approvals queue, so the menu's Approvals balloon reads again at once.
    refreshMenuCounts();
    setNotice(ok);
    page.reload();
  };

  return (
    <AsyncStatePanel state={page.state} emptyAction={t('document.notFound')} onRetry={page.reload}>
      {(change) => {
        /*
         * 🔒 Whether this viewer may approve is the page answer's own `canApprove`,
         * which the server resolves with the Pending Approvals queue's own function
         * (decision 0504; T644, decision 1521): two resolvers for *may this person
         * approve* is how a control appears for somebody the route will refuse.
         * 🔨 A viewer the queue's read refuses sees neither control, which is what
         * the approvals capability means.
         */
        const mayApprove = change.canApprove === true;
        const mayResubmit = change.approvalStatus === 'Rejected' && change.status !== 'Withdrawn' && !change.writeRefusal;
        const facts: ScreenFact[] = [
          { label: t('changeOrder.fact.order'), value: change.salesOrderNumber, route: `/sales-orders/${change.salesOrderId}` },
          { label: t('changeOrder.fact.account'), value: change.companyName },
          { label: t('changeOrder.fact.sequence'), value: String(change.sequence) },
          { label: t('changeOrder.fact.total'), value: <Money cents={change.totalCents} currencyCode={change.currencyCode} /> },
          { label: t('changeOrder.fact.created'), value: dayText(change.createdAt) },
        ];
        const footer = (
          <>
            {/*
             * 🔒 The server decides which edit this change order allows and the page
             * asks it (decision 0474). What freezes it is a dependant, never an
             * approval (decision 0486), and the refusal is written on the footer
             * rather than hidden behind a control that is simply absent.
             */}
            {/* 🔨 Edit is the action bar's primary and is drawn there alone (decision 0612). */}
            {/*
              * 🔒 The three acts a change order is worked with, on the page it is read on (decision 0504). Approve and
              * Reject are the queue's own two routes; Withdraw was reachable from nothing at all before this, so a change
              * order raised in error stood on its order forever.
              */}
            {mayApprove ? (
              <Button size={Size.Medium} color={Color.Navy} text={[t('approvals.approve')]} actions={[() => void act(approvalActionPath('ChangeOrder', change.id, 'approve'), {}, t('changeOrder.approved'))]} disabled={busy} />
            ) : null}
            {/* 🔒 A rejected change order is corrected and resubmitted from its own page, as the Bill is (T1022); the reason it was rejected stands above the lines. */}
            {mayResubmit ? (
              <Button size={Size.Medium} color={Color.Navy} text={[t('changeOrder.action.resubmit')]} actions={[() => void act(`${CHANGE_ORDERS_PATH}/${change.id}/resubmit`, {}, t('changeOrder.resubmitted'))]} disabled={busy} />
            ) : null}
            {mayApprove ? <Button size={Size.Medium} color={Color.Inactive} text={[t('approvals.reject')]} actions={[() => openModal('reject')]} disabled={busy} /> : null}
            {change.mayWithdraw ? <Button size={Size.Medium} color={Color.Inactive} text={[t('changeOrder.action.withdraw')]} actions={[() => openModal('withdraw')]} disabled={busy} /> : null}
            <Button size={Size.Medium} color={Color.Inactive} text={[t('changeOrder.backToOrder')]} actions={[() => navigate(`/sales-orders/${change.salesOrderId}`)]} />
            {change.frozenReason ? <span className={styles.refusal} data-change-order-refusal="true">{change.frozenReason}</span> : null}
            {/* 🔨 The sheet's own control moved to the action bar above both views, so one document offers it once (decision 0612). */}
            {/* 🔨 Withheld with the reason, not simply absent: a cleared change order is reversed by a further one and the page says so (decision 0410). */}
            {change.withdrawRefusal ? <span className={styles.refusal} data-change-order-withdraw-refusal="true">{change.withdrawRefusal}</span> : null}
            {/* 🔨 A cleared change order still edits, and the cost of doing so is said before the person starts (decision 0486). */}
            {change.editable && change.reopenWarning ? <span className={styles.refusal} data-change-order-reopen-warning="true">{change.reopenWarning}</span> : null}
          </>
        );
        const moneyOf = (amount: number) => <Money cents={amount} currencyCode={change.currencyCode} />;
        /** 🔒 The change's own lines as the one read-only table draws them (decision 0612). */
        const lines: DocumentLinesDescriptor = {
          title: t('changeOrder.lines.title'),
          rowAttribute: 'data-change-order-line',
          blockAttribute: 'data-change-order-lines',
          empty: t('changeOrder.needsLines'),
          rows: change.lines.map((row) => ({
            id: row.id,
            label: row.sku ? line(row.sku, '·', row.description) : row.description,
            note: row.taxCategoryName,
            // 🔨 A negative quantity reads as it was typed: it is how a removal is recorded (decision 0123).
            cells: [line(String(row.quantity), row.unitName), row.unitPriceCents === null ? '' : moneyOf(row.unitPriceCents)],
            amount: moneyOf(row.amountCents),
            attributes: { 'data-change-order-quantity': String(row.quantity) },
          })),
          // 🔨 The total is the server's own figure over the stored lines, never a sum worked out on the screen.
          totals: [{ label: t('changeOrder.lines.total'), value: moneyOf(change.totalCents), attributes: { 'data-change-order-total': String(change.totalCents) } }],
        };
        // 🔒 One notes panel, placed in the Details view and handed to the PDF view's rail; only one view is mounted (T587; decision 1403).
        const carried = carriedNotes(change);
        const notesPanel = <DocumentNotes recordType="ChangeOrder" recordId={change.id} recordLabel={change.changeOrderNumber} apiBaseUrl={apiBaseUrl} onError={(message) => setNotice(`!${message}`)} carried={carried} />;
        // 🔒 The files attached to this ChangeOrder, one block placed in the Details view (decision 0457; T1071).
        const filesPanel = <DocumentFilesSection recordType="ChangeOrder" recordId={change.id} writeRefusal={change.writeRefusal ?? null} apiBaseUrl={apiBaseUrl} />;
        return (
          <DocumentViewPage
            notes={carried === null ? null : notesPanel}
            files={filesPanel}
            documentType="ChangeOrder"
            icon="terms"
            title={change.changeOrderNumber}
            subtitle={pageKindLineText({ page: 'document', documentType: 'ChangeOrder' })}
            information={facts}
            status={<BannerPills pills={changeOrderPills(change, resolveStatus)} attribute="data-change-order-pills" hint={change.approval?.reason ?? null} />}
            footer={footer}
            footerAttributes={{ 'data-change-order-actions': change.editable ? 'edit' : 'none' }}
            pageAttributes={{ 'data-change-order': change.id, 'data-change-order-status': change.status, 'data-change-order-approval': change.approvalStatus }}
            layout={change.printLayout ?? null}
            sheet={change.printSheet ?? null}
            writeRefusal={change.writeRefusal ?? null}
            ribbon={change.status}
            pdfPath={documentPdfPath(CHANGE_ORDERS_PATH, change.id)}
            listRoute={`/sales-orders/${change.salesOrderId}`}
            {...(change.editable ? { onEdit: () => navigate(`${CHANGE_ORDER_ROUTE}/${change.id}?mode=edit`) } : {})}
            apiBaseUrl={apiBaseUrl}
            terms={{ documentId: change.id, onSaved: page.reload }}
            lines={lines}
            notices={
              <>
                {notice ? <AlertBanner tone={notice.startsWith('!') ? 'error' : 'good'} message={notice.replace(/^!/, '')} action={{ label: t('modal.dismiss'), onAction: () => setNotice(null) }} /> : null}
                {/* 🔒 The approval block says why the change order rests where it does (T131; decision 0713); an older answer carries the escalation alone. */}
                {change.approval ? <ApprovalReason view={change.approval} /> : change.escalationReason ? <p className={styles.sentence} data-change-order-escalation>{line(t('po.escalation'), '·', change.escalationReason)}</p> : null}
                {change.rejectionReason ? <p className={styles.rejection} data-change-order-rejection>{line(t('po.rejection'), '·', change.rejectionReason)}</p> : null}
              </>
            }
            details={
              <>
              {/* 🔒 The Deal's team as it stood when this document was created (decision 0765). */}
              <DealTeamPanel team={change.dealTeam} timeZone={zone} />
              <section className={styles.block} data-change-order-prose="true">
                <h2 className={styles.heading}>{t('changeOrder.reason')}</h2>
                <p className={styles.sentence}>{change.reason}</p>
                <h2 className={styles.heading}>{t('changeOrder.description')}</h2>
                <p className={styles.sentence}>{change.description}</p>
                <p className={styles.sentence}>
                  <Link to={`/sales-orders/${change.salesOrderId}`}>{change.salesOrderNumber}</Link>
                </p>
              </section>
              {filesPanel}
              {/* 🔒 The document's notes, the composer at the top of the list (decision 0951; T336). */}
              {notesPanel}
              </>
            }
          >
              {open === 'withdraw' ? (
                <ReasonWindow
                  open
                  title={t('changeOrder.withdraw.title')}
                  label={t('changeOrder.withdraw.reason')}
                  actLabel={t('changeOrder.action.withdraw')}
                  hint={t('changeOrder.withdraw.hint')}
                  busy={busy}
                  onCancel={() => setOpen(null)}
                  refusal={notice?.startsWith('!') ? notice.slice(1) : null}
                  dataModal="change-order-withdraw"
                  onAct={(reason) => act(`${CHANGE_ORDERS_PATH}/${change.id}/withdraw`, { reason }, t('changeOrder.withdrawn'))}
                />
              ) : null}
              {open === 'reject' ? (
                <ReasonWindow
                  open
                  title={t('approvals.reject')}
                  label={t('approvals.rejectReason')}
                  actLabel={t('approvals.reject')}
                  busy={busy}
                  onCancel={() => setOpen(null)}
                  refusal={notice?.startsWith('!') ? notice.slice(1) : null}
                  dataModal="change-order-reject"
                  onAct={(reason) => act(approvalActionPath('ChangeOrder', change.id, 'reject'), { reason }, t('changeOrder.rejected'))}
                />
              ) : null}
          </DocumentViewPage>
        );
      }}
    </AsyncStatePanel>
  );
}

/** The route element: the change order named in the path, or the one being raised against the order the address carries. */
export function ChangeOrderPageRoute({ apiBaseUrl = '' }: { apiBaseUrl?: string }) {
  const { id = '' } = useParams();
  return <ChangeOrderPage key={id} changeOrderId={id} apiBaseUrl={apiBaseUrl} />;
}
